import {
  select as d3Select,
  timeFormatDefaultLocale as d3TimeFormatDefaultLocale,
  map as d3Map,
  extent as d3Extent,
  bisector as d3Bisector,
  pointer as d3Pointer,
  scaleOrdinal as d3ScaleOrdinal,
  scaleTime as d3ScaleTime,
  scaleLinear as d3ScaleLinear,
  axisBottom as d3AxisBottom,
  line as d3Line,
  range as d3Range,
  axisLeft as d3AxisLeft,
  timeDay as d3TimeDay,
  timeMonth as d3TimeMonth,
  curveCardinal as d3CurveCardinal,
  format as d3Format
} from 'd3'

import {
  neutralColorHighPure,
  neutralColorHighDark,
  neutralColorLowLight
} from '@deskfy/unify-design-tokens/jsvariables/index.js'

import {
  CONSTANTS,
  checkWidthScreen
} from './utils'

import {
  getJSONLocaleDate,
  parseTimezoneDate,
  addDateWeek,
  diffInDays,
  diffInMonths,
  diffInWeeks
} from '../../utils'

/* @ngInject */
function AnalyticsDataFrameLineChartController ($rootScope, $scope, $sce, $state, $location, $attrs, $window, $translate, AnalyticsService, SegmentService, Utils) {
  const api = {
    _loadDataFrameData,
    _successGetResponse,
    _errorGetResponse,
    _finallyGetResponse,
    _insertParamsAndLoadData,
    _getUserLocale,
    _successGetUserLocale,
    _handleLineChart,
    _mountCheckboxes,
    _isDisableDateFormat,
    _mountDropdowns,
    _insertLineChart,
    _toggleLineChart,
    _updateData,
    _handleCheckboxForm,
    _isHandleDateFormat,
    _insertChart,
    _insertChartMouse,
    _insertTooltip,
    _insertGroupArrow,
    _insertGroupMinus,
    _insertMousePerLine,
    _makeLineData,
    _makeDates,
    _makeXAxis,
    _getXTicksStep,
    _makeYAxis,
    _makeLines,
    _handleResizeLineChart,
    _getDefaultDateFormat
  }

  $scope.flags = {
    loading: true,
    emptyChart: false,
    hasError: false,
    statusTooltip: '',
    isFirstLoad: true,
    disableCheckbox: ''
  }

  $scope.components = {
    checkbox: [],
    dropdown: []
  }

  const dateFormat = api._getDefaultDateFormat($scope.data.vars.list)

  $scope.params = {
    start: '',
    end: '',
    dateFormat
  }

  $scope.form = {
    checkbox: {},
    dropdown: {
      dateFormat
    }
  }

  $scope.dataFrameSlug = ''
  $scope.description = ''

  const isCampaign = $state.current.name.includes('campaign')
  const slug = $state.params.slug || $scope.slug

  this.getApi = () => api

  activate()

  function activate () {
    api._getUserLocale()
    api._handleResizeLineChart()
    CONSTANTS.width = checkWidthScreen($window.innerWidth, null, $scope.fullWidth)
    $scope.dataFrameTruncateName = Utils.truncateText($translate.instant($scope.data.name), 64)
    $scope.dataFrameSlug = $scope.data.slug
  }

  $scope.$watchCollection('filterParams', api._insertParamsAndLoadData)
  $scope.$watchCollection('form.checkbox', api._handleCheckboxForm)

  $scope.loadDataFrameData = () => {
    return api._loadDataFrameData(slug, $scope.data.id)
  }

  $scope.selectFilterChange = option => {
    const { label, value } = option

    $scope.params.dateFormat = value
    $scope.form.dropdown.dateFormat = value
    api._loadDataFrameData(slug, $scope.data.id)

    SegmentService.track('analytics-dataframe-viewchange', {
      data_frame: $scope.dataFrameSlug,
      view_name: label
    })
  }

  /**
   * This method call the method for get data of card
   *
   * @param {String} paramsSlug
   * @param {Number} dataFrameId
   */
  function _loadDataFrameData (paramsSlug, dataFrameId) {
    $scope.flags.loading = true
    $scope.loadingDataFrames.lineChart = true

    $scope.flags.hasError = false

    AnalyticsService.getDataFrameDataFromId(paramsSlug, dataFrameId, $scope.params)
      .then(api._successGetResponse)
      .catch(api._errorGetResponse)
      .finally(api._finallyGetResponse)
  }

  /**
   * This method load the values when request is successful.
   *
   * check if exists data and added values to card, if not exists added empty values to card
   *
   * @param {object} response
   */
  function _successGetResponse (response) {
    $scope.flags.emptyChart = false

    const {
      data: { result }
    } = response

    const checkLineGroup = $scope.data.vars.checkLineGroup
    const list = $scope.data.vars.list
    const legend = $scope.data.vars.legend

    const { start, end } = $scope.params

    if (!result.length || start === end) {
      $scope.flags.emptyChart = true
      return
    }

    if (checkLineGroup?.length) {
      $scope.lineColors = checkLineGroup.sort((a, b) => a.order - b.order).map(item => item.color)
    }

    if (legend?.length) {
      $scope.lineColors = legend.sort((a, b) => a.order - b.order).map(item => item.color)
    }

    if (response.description) {
      $scope.description = $sce.trustAsHtml(response.description)
    }

    api._mountCheckboxes(checkLineGroup)

    $scope.components = {
      checkbox: checkLineGroup,
      dropdown: api._mountDropdowns(list),
      legend
    }

    api._handleLineChart(result)
  }

  /**
   * This method show the error and added empty values for when request fails
   */
  function _errorGetResponse () {
    $scope.flags.hasError = true
  }

  function _finallyGetResponse () {
    $scope.flags.loading = false
    $scope.loadingDataFrames.lineChart = false
  }

  /**
   * This method get new values from filter params and load the data
   *
   * Is called when filter params object is changed
   *
   * @param {object} newValues
   */
  function _insertParamsAndLoadData (newValues) {
    if (!newValues.filtersLoaded) return

    const { calendar: { start, end } } = newValues

    const { dateFormat: currentDateFormat } = $scope.form.dropdown
    const calendarDaysDiff = diffInDays(start, end)
    const isCalendarHandled = start !== $scope.params.start || end !== $scope.params.end
    const newDateFormat = api._isHandleDateFormat(calendarDaysDiff, isCalendarHandled)

    const haveDateParameters = ($location.search()?.periodStart && $location.search()?.periodEnd) || isCampaign

    $scope.params = {
      ...$scope.params,
      ...newValues,
      dateFormat: $scope.form.dropdown.dateFormat,
      start,
      end
    }

    if (haveDateParameters && newDateFormat && newDateFormat !== currentDateFormat) {
      $scope.form.dropdown.dateFormat = newDateFormat
      $scope.params.dateFormat = newDateFormat
    }

    api._loadDataFrameData(slug, $scope.data.id)
  }

  function _isHandleDateFormat (calendarDaysDiff, isCalendarHandled) {
    const rangeOfDaily = 30
    const rangeOfWeekly = 60

    if (isCalendarHandled) {
      if (calendarDaysDiff < rangeOfDaily) {
        return 'DAILY'
      }

      if (calendarDaysDiff < rangeOfWeekly) {
        return 'WEEKLY'
      }

      return 'MONTHLY'
    }
  }

  function _handleCheckboxForm (newValue) {
    const { chartWrapperElement, classNames } = CONSTANTS
    const classes = classNames(true)
    const svg = d3Select(chartWrapperElement($attrs))
    const chart = svg.select(classes.chart)
    const transparentChartLine = chart.selectAll(classes.transparentChartLine)

    const checkboxEntries = Object.entries(newValue)
    const isAllChecked = checkboxEntries.filter(([_, value]) => value)

    if (isAllChecked?.length === 1) {
      const [name] = isAllChecked[0]
      $scope.flags.disableCheckbox = name
    } else {
      $scope.flags.disableCheckbox = ''

      if (transparentChartLine.nodes().length > 1) {
        transparentChartLine.style('stroke-width', '50px')
      }
    }

    Object.keys(newValue).forEach(api._toggleLineChart)
  }

  function _getUserLocale () {
    $rootScope.getUser().then(api._successGetUserLocale)
  }

  function _successGetUserLocale ({ locale }) {
    $scope.locale = locale
  }

  function _handleLineChart (result) {
    if ($scope.flags.isFirstLoad) {
      api._insertLineChart(result)
      $scope.flags.isFirstLoad = false
    } else {
      api._updateData(result)
    }
  }

  function _mountCheckboxes (checkLineGroup) {
    if ($scope.flags.isFirstLoad && checkLineGroup?.length) {
      checkLineGroup.forEach(checkbox => {
        $scope.form.checkbox[checkbox.name] = checkbox.defaultValue === 'TRUE'
      })
    }
  }

  function _mountDropdowns (dropdownList) {
    const notHaveDateParameters = (!$location.search()?.periodStart && !$location.search()?.periodEnd) && !isCampaign

    if (dropdownList?.length) {
      return dropdownList.map(({ defaultValue, queryValue, translation }) => {
        if (defaultValue === 'TRUE' && $scope.flags.isFirstLoad && notHaveDateParameters) {
          $scope.params.dateFormat = queryValue
        }

        return {
          label: $translate.instant(translation),
          value: queryValue,
          disabled: api._isDisableDateFormat(queryValue),
          active: (
            notHaveDateParameters && $scope.flags.isFirstLoad && defaultValue === 'TRUE'
          ) || (
            $scope.form.dropdown.dateFormat === queryValue
          )
        }
      })
    }

    return []
  }

  function _isDisableDateFormat (dateFormat) {
    const { start, end } = $scope.params
    const calendarDaysDiff = diffInDays(start, end)

    const minLimitOfMonthly = 32

    const minLimitOfWeekly = 14
    const maxLimitOfWeekly = 90

    const minLimitOfDaily = 2
    const maxLimitOfDaily = 31

    const isDisableMonthly = dateFormat === 'MONTHLY' && calendarDaysDiff < minLimitOfMonthly
    const isDisableWeekly =
      dateFormat === 'WEEKLY' && (calendarDaysDiff < minLimitOfWeekly || calendarDaysDiff > maxLimitOfWeekly)
    const isDisableDaily =
      dateFormat === 'DAILY' && (calendarDaysDiff < minLimitOfDaily || calendarDaysDiff > maxLimitOfDaily)

    return isDisableMonthly || isDisableWeekly || isDisableDaily
  }

  function _toggleLineChart (name) {
    const { classNames, chartWrapperElement } = CONSTANTS
    const classes = classNames(true)

    const svgElement = chartWrapperElement($attrs)
    const linePath = d3Select(svgElement).selectAll(classes.lines(name))

    if ($scope.form.checkbox[name]) {
      linePath.style('display', 'block')
    } else {
      linePath.style('display', 'none')
    }
  }

  function _insertChart (svg) {
    const { width, height, svgHeight, classNames } = CONSTANTS
    const classes = classNames(false)

    const chart = svg
      .append('g')
      .attr('width', width)
      .attr('height', svgHeight)
      .attr('transform', 'translate(60, 40)')
      .attr('class', classes.chart)

    chart
      .append('g')
      .attr('class', classes.xAxis)
      .attr('transform', 'translate(0,' + height + ')')

    chart.append('g').attr('class', classes.yAxis)

    return chart
  }

  function _insertChartMouse (svg) {
    const { classNames } = CONSTANTS
    const classes = classNames(false)

    const chartMouse = svg
      .append('g')
      .attr('class', classes.mouse)
      .attr('transform', 'translate(60, 40)')
      .style('display', 'none')

    chartMouse.append('path').attr('class', classes.mouseLine)

    return chartMouse
  }

  function _insertTooltip (chartMouse) {
    const { classNames, widthTooltip, xPositionTooltip } = CONSTANTS
    const classes = classNames(false)
    const chartTooltip = chartMouse.append('g').attr('class', classes.tooltip.main)

    chartTooltip
      .append('rect')
      .attr('width', widthTooltip)
      .attr('height', 140)
      .style('fill', neutralColorHighPure)
      .style('stroke', neutralColorHighDark)
      .style('stroke-width', '1px')
      .attr('x', -xPositionTooltip)
      .attr('y', -180)
      .attr('rx', 8)
      .attr('ry', 8)

    chartTooltip
      .append('text')
      .attr('class', classes.tooltip.name)
      .attr('x', -2)
      .attr('y', -152)

    chartTooltip.append('text').attr('class', classes.tooltip.date)

    chartTooltip
      .append('text')
      .attr('class', classes.tooltip.value)
      .attr('x', -26)
      .attr('y', -100)

    chartTooltip
      .append('text')
      .attr('class', classes.tooltip.percent)
      .attr('x', -8)
      .attr('y', -60)

    chartTooltip.append('text').attr('class', classes.tooltip.compareText)

    chartTooltip
      .append('rect')
      .attr('width', 16)
      .attr('height', 16)
      .attr('class', classes.tooltip.color)
      .attr('x', -25)
      .attr('y', -165)
      .attr('rx', 100)
      .attr('ry', 100)

    return chartTooltip
  }

  function _insertGroupArrow (chartTooltip) {
    const { classNames, drawnPath } = CONSTANTS
    const classes = classNames(false)
    const groupArrow = chartTooltip.append('g').attr('class', classes.groupArrow)

    groupArrow
      .append('path')
      .attr('fill-rule', 'evenodd')
      .attr('clip-rule', 'evenodd')
      .attr('d', drawnPath.groupArrow[0])

    groupArrow
      .append('path')
      .attr('fill-rule', 'evenodd')
      .attr('clip-rule', 'evenodd')
      .attr('d', drawnPath.groupArrow[1])
  }

  function _insertGroupMinus (chartTooltip) {
    const { classNames, drawnPath } = CONSTANTS
    const classes = classNames(false)
    const groupMinus = chartTooltip.append('g').attr('class', classes.groupMinus)

    groupMinus
      .append('path')
      .attr('fill', neutralColorLowLight)
      .attr('fill-rule', 'evenodd')
      .attr('clip-rule', 'evenodd')
      .attr('d', drawnPath.groupMinus)
  }

  function _insertMousePerLine (chartMouse) {
    const { classNames } = CONSTANTS
    const classes = classNames(false)
    const mousePerLine = chartMouse.append('g').attr('class', classes.mousePerLine)

    mousePerLine
      .append('circle')
      .attr('r', 10)
      .style('stroke', neutralColorHighDark)
      .style('fill', neutralColorHighPure)
      .style('stroke-width', '1px')

    mousePerLine
      .append('circle')
      .attr('r', 6)
      .attr('class', classes.circleColor)
  }

  function _insertLineChart (result) {
    const { chartWrapperElement, width, height } = CONSTANTS

    // select and create svg
    const svg = d3Select(chartWrapperElement($attrs))
      .append('svg')
      .attr('width', width)
      .attr('height', height)

    api._insertChart(svg)
    const chartMouse = api._insertChartMouse(svg)
    const chartTooltip = api._insertTooltip(chartMouse)
    api._insertGroupArrow(chartTooltip)
    api._insertGroupMinus(chartTooltip)
    api._insertMousePerLine(chartMouse)
    api._updateData(result)
  }

  function _makeLineData (result) {
    return d3Map(result, data => {
      const keysData = Object.keys(data)
        .filter(key => !['maxValue', 'isIncompletePeriod'].includes(key))
        .reduce((acc, key) => {
          acc[key] = data[key].total
          return acc
        }, {})

      const { date } = Object.values(data).filter(value => value?.date)[0]

      return {
        ...keysData,
        date: parseTimezoneDate(date.slice(0, 10))
      }
    })
  }

  function _makeDates (result) {
    const rawStartDate = parseTimezoneDate(result[0].firstDate)
    const rawEndDate = parseTimezoneDate(result[0].lastDate)

    const startDate = new Date(rawStartDate)
    const parseEndDate = new Date(rawEndDate)

    const startSelectedDate = parseTimezoneDate($scope.params.start)
    const endSelectedDate = parseTimezoneDate($scope.params.end)

    return { startDate, endDate: rawEndDate, parseEndDate, startSelectedDate, endSelectedDate }
  }

  function _makeXScale (lineData) {
    const { width } = CONSTANTS

    return d3ScaleTime()
      .domain(d3Extent(lineData, d => d.date))
      .range([0, width])
  }

  function _makeXAxis (result, localization, chart, xScale) {
    const { classNames } = CONSTANTS
    const { dateFormat } = $scope.form.dropdown
    const xTicksFormat = dateFormat !== 'MONTHLY' ? '%d %b' : '%b %y'
    const classes = classNames(true)

    const lineData = api._makeLineData(result)
    const actualDates = lineData.map(d => d.date)

    const xAxisGenerator = d3AxisBottom(xScale)
      .tickSize(0)
      .tickValues(actualDates)
      .tickFormat(localization.format(xTicksFormat))

    const xAxis = chart.selectAll(classes.xAxis).call(xAxisGenerator)

    xAxis.selectAll(classes.tickText).attr('dy', '29px')
  }

  function _getXTicksStep (result, dateFormat) {
    const { startDate, endDate, parseEndDate } = api._makeDates(result)

    const { start, end } = $scope.params

    const diffMonths = diffInMonths(startDate, endDate)
    const diffWeeks = diffInWeeks(startDate, endDate)
    const diffDays = diffInDays(start, end)
    const limit = 15

    const dateFormats = {
      MONTHLY: () => {
        const monthlyStep = diffMonths > limit ? 4 : 1
        return d3TimeMonth.range(startDate, parseEndDate, monthlyStep)
      },
      WEEKLY: () => {
        const weeklyStep = diffWeeks > limit ? 14 : 7

        const range = []
        const date = new Date(startDate)

        while (date <= parseEndDate) { // eslint-disable-line no-unmodified-loop-condition
          const isFirstDate = date.getTime() === startDate.getTime()
          const isLastDate = date.getTime() === parseEndDate.getTime()
          const isMonday = date.getDay() === 1

          if (!isMonday && !isFirstDate && !isLastDate) {
            const daysToMonday = (date.getDay() + 6) % 7
            date.setDate(date.getDate() - daysToMonday)
          }
          range.push(new Date(date))

          date.setDate(date.getDate() + weeklyStep)
        }

        return range
      },
      DAILY: () => {
        const dailyStep = diffDays > limit ? 2 : 1
        return d3TimeDay.range(startDate, parseEndDate, dailyStep)
      }
    }

    return dateFormats[dateFormat]()
  }

  function _makeYScale (maxValue) {
    const { height } = CONSTANTS

    return d3ScaleLinear()
      .domain([0, maxValue])
      .range([height, 0])
      .nice()
  }

  function _makeColorScale (lineData) {
    const ignoredKeys = ['date', 'firstDate', 'lastDate']

    return d3ScaleOrdinal()
      .domain(Object.keys(lineData[0]).filter(key => !ignoredKeys.includes(key)))
      .range($scope.lineColors)
  }

  function _makeYAxis (result, chart, yScale) {
    const maxValue = result[0].maxValue
    const { numberOfTicks, width, classNames } = CONSTANTS
    const classes = classNames(true)

    const maxValueDivider = Number('1'.padEnd(String(maxValue).length - 1, '0'))
    const ticksStep = Math.ceil(maxValue / numberOfTicks / maxValueDivider) * maxValueDivider
    const maxTickValue = (Math.ceil(maxValue / maxValueDivider) * maxValueDivider) + ticksStep

    const yAxisGenerator = d3AxisLeft(yScale)
      .ticks(numberOfTicks)
      .tickValues(d3Range(0, maxTickValue, ticksStep))
      .tickSize(-width)
      .tickFormat(d3Format('d'))

    const yAxis = chart.selectAll(classes.yAxis).call(yAxisGenerator)
    yAxis.selectAll(classes.tickText).attr('dx', '-10px')
    yAxis.select(classes.domain).remove()
  }

  function _makeLines (color, result) {
    const ignoredKeys = ['firstDate', 'lastDate', 'isIncompletePeriod']
    return color.domain().map(key => ({
      name: key,
      values: result.map(data => {
        if (!ignoredKeys.includes(key) && data[key]) {
          return {
            date: parseTimezoneDate(data[key].date.slice(0, 10)),
            value: data[key].total,
            previous: data[key].previous,
            color: data[key].color,
            unitType: data[key]?.unitType,
            isIncompletePeriod: data?.isIncompletePeriod
          }
        }
      }).filter(Boolean)
    }))
  }

  function _updateData (result) {
    const { chartWrapperElement, width, height, classNames, percentOfTension } = CONSTANTS

    const classes = classNames(true)
    const classesWithoutDot = classNames(false)
    const svg = d3Select(chartWrapperElement($attrs))
    const chart = svg.select(classes.chart)
    const localization = d3TimeFormatDefaultLocale(getJSONLocaleDate($scope.locale))
    const lineData = api._makeLineData(result)
    const maxValue = result[0].maxValue
    const color = _makeColorScale(lineData)
    const xScale = _makeXScale(lineData)
    const yScale = _makeYScale(maxValue)

    api._makeXAxis(result, localization, chart, xScale)
    api._makeYAxis(result, chart, yScale)
    const lines = api._makeLines(color, result)

    const lineGenerator = d3Line()
      .x(data => xScale(data.date))
      .y(data => yScale(data.value))
      .curve(d3CurveCardinal.tension(percentOfTension))

    const plottedChartLines = chart.selectAll(classes.plottedChartLine).data(lines)
    const transparentChartLine = chart.selectAll(classes.transparentChartLine).data(lines)
    const chartMouse = svg.select(classes.mouse)
    const chartTooltip = chartMouse.select(classes.tooltip.main)
    const pointer = chartMouse.select(classes.mousePerLine)
    const groupArrow = chartTooltip.select(classes.groupArrow)
    const groupMinus = chartTooltip.select(classes.groupMinus)

    plottedChartLines
      .enter()
      .append('path')
      .attr('class', d => `${classesWithoutDot.plottedChartLine} ${classesWithoutDot.lines(d.name)}`)
      .merge(plottedChartLines)
      .attr('d', d => lineGenerator(d.values))
      .style('stroke', d => color(d.name))
      .style('fill', 'none')
      .style('stroke-width', '2px')
      .on('mouseover', lineHover)
      .on('mousemove', lineHover)

    transparentChartLine
      .enter()
      .append('path')
      .attr('class', d => `${classesWithoutDot.transparentChartLine} ${classesWithoutDot.lines(d.name)}`)
      .merge(transparentChartLine)
      .attr('d', d => lineGenerator(d.values))
      .style('stroke', d => color(d.name))
      .style('fill', 'none')
      .style('opacity', 0)
      .style('stroke-width', '50px')
      .on('mouseover', lineHover)
      .on('mousemove', lineHover)

    chart.on('mouseout', event => {
      if (
        event.fromElement.classList.contains('chart-line') &&
        event.toElement !== null &&
        event.fromElement !== event.toElement &&
        event.toElement.nodeName === 'circle'
      ) {
        return
      }

      chart.selectAll(classes.plottedChartLine).style('stroke-width', '2px')
      chartMouse.style('display', 'none')
    })

    function getValueOfLine (xDate, firstHoverValue, secondHoverValue) {
      if (!secondHoverValue?.date) {
        return firstHoverValue
      }

      return xDate - firstHoverValue.date > secondHoverValue.date - xDate ? secondHoverValue : firstHoverValue
    }

    const bisectDate = d3Bisector(data => data.date).left

    function lineHover (event, hoverData) {
      chartMouse.style('display', null)
      const mouse = d3Pointer(event)
      const xDate = xScale.invert(mouse[0])

      const indexData = bisectDate(hoverData.values, xDate, 1)
      const firstHoverValue = hoverData.values[indexData - 1]
      const secondHoverValue = hoverData.values[indexData]

      const valueOfLine = getValueOfLine(xDate, firstHoverValue, secondHoverValue)
      const xScalePosition = xScale(valueOfLine.date)
      const yScalePosition = yScale(valueOfLine.value)

      let draw = 'M' + xScalePosition + ',' + 0
      draw += ' ' + xScalePosition + ',' + height

      const xTooltipPosition = xScalePosition > width * 0.8 ? xScalePosition - 230 : xScalePosition
      chartTooltip.attr('transform', 'translate(' + xTooltipPosition + ',' + yScalePosition + ')')
      pointer.attr('transform', 'translate(' + xScalePosition + ',' + yScalePosition + ')')
      pointer.select(classes.circleColor).style('fill', valueOfLine.color)

      chartMouse.select(classes.mouseLine).attr('d', draw)
      chart.selectAll(classes.plottedChartLine).style('stroke-width', '2px')
      chart.select(classes.lines(hoverData.name)).style('stroke-width', '4px')

      updateTooltips(valueOfLine, hoverData)
    }

    function getPreviousPercentage (valueOfLine) {
      const { previous, value } = valueOfLine

      const unitTypes = {
        INTEGER: () => {
          const expression = (value - previous) / previous
          return expression
        },
        PERCENTAGE: () => {
          const expression = (value - previous) / 100
          return expression
        },
        DEFAULT: () => {
          const expression = (value - previous) / previous
          return expression
        }
      }

      if (!value || !previous) {
        $scope.flags.statusTooltip = 'no-available'
        return $translate.instant('dataFrames.lineChart.tooltips.noAvailableInitials')
      }

      const expression = unitTypes[valueOfLine.unitType]?.() || unitTypes.DEFAULT()

      if (value === previous) {
        $scope.flags.statusTooltip = 'no-variation'
        return '0%'
      }

      if (value > previous) {
        $scope.flags.statusTooltip = 'positive'
      } else {
        $scope.flags.statusTooltip = 'negative'
      }

      if (valueOfLine.unitType === 'INTEGER' && expression >= 10) {
        return '999+%'
      }

      return expression.toLocaleString($scope.locale, { style: 'percent' }).replace(/-(?=\d)/, '')
    }

    function getCheckboxesName () {
      if ($scope.components.checkbox?.length) {
        return $scope.components.checkbox.reduce((acc, line) => {
          if (acc) {
            acc[line.name] = $translate.instant(line.translation)
          }

          return acc
        }, {})
      }

      if ($scope.components.legend?.length) {
        return $scope.components.legend.reduce((acc, line) => {
          if (acc) {
            acc[line.name] = $translate.instant(line.translation)
          }

          return acc
        }, {})
      }

      return {}
    }

    function updateTooltips (valueOfLine, data) {
      const { dateFormat } = $scope.form.dropdown
      const { statusTooltip } = $scope.flags
      const checkboxesName = getCheckboxesName()
      const previousPercentage = getPreviousPercentage(valueOfLine)
      const {
        xAxisFormats,
        arrowRotation,
        getXPositionFromBBox,
        widthTooltip,
        xPositionTooltip,
        paddingTooltip
      } = CONSTANTS
      const parseTime = localization.format(xAxisFormats[dateFormat])
      const { endSelectedDate } = api._makeDates(result)

      const date =
        dateFormat !== 'WEEKLY'
          ? parseTime(valueOfLine.date)
          : `${parseTime(valueOfLine.date)} - ${parseTime(addDateWeek(valueOfLine.date, endSelectedDate))}`

      chartTooltip.select(classes.tooltip.color).style('fill', valueOfLine.color)

      const tooltipNameLimit = dateFormat === 'MONTHLY' ? 27 : 17 // add more ... (30 and 20)
      const tooltipName = Utils.truncateText(checkboxesName[data.name], tooltipNameLimit)

      chartTooltip.select(classes.tooltip.name).text(tooltipName)

      const dateTextElement = chartTooltip.select(classes.tooltip.date).text(date)

      const xPositionCompareText = getXPositionFromBBox(
        dateTextElement.node(),
        paddingTooltip,
        widthTooltip,
        xPositionTooltip
      )
      dateTextElement.attr('transform', `translate(${xPositionCompareText}, -152)`)

      const insertValueByType = {
        PERCENTAGE: () => {
          const parsedValue = parseInt(valueOfLine.value, 10) / 100
          chartTooltip.select(classes.tooltip.value).text(parsedValue.toLocaleString($scope.locale, {
            style: 'percent'
          }))
        },
        INTEGER: () => {
          chartTooltip.select(classes.tooltip.value).text(valueOfLine.value.toLocaleString($scope.locale))
        },
        DEFAULT: () => {
          chartTooltip.select(classes.tooltip.value).text(valueOfLine.value.toLocaleString($scope.locale))
        }
      }

      if (insertValueByType[valueOfLine.unitType]) {
        insertValueByType[valueOfLine.unitType]()
      } else {
        insertValueByType.DEFAULT()
      }

      if (dateFormat !== 'DAILY' && valueOfLine.isIncompletePeriod) {
        const translationKey = dateFormat === 'MONTHLY' ? 'incompleteMonthMessage' : 'incompleteWeekMessage'

        chartTooltip
          .select(classes.tooltip.compareText)
          .text($translate.instant(`dataFrames.lineChart.labels.${translationKey}`))
          .attr('transform', 'translate(-25, -60)')

        chartTooltip.select(classes.tooltip.percent).style('opacity', '0')
        groupArrow.style('opacity', '0')
        groupMinus.style('opacity', '0')
      } else {
        const compareTextEl = chartTooltip
          .select(classes.tooltip.compareText)
          .text($translate.instant('dataFrames.lineChart.labels.previousPeriodText'))

        const xPositionCompareText = getXPositionFromBBox(
          compareTextEl.node(),
          paddingTooltip,
          widthTooltip,
          xPositionTooltip
        )
        compareTextEl.attr('transform', `translate(${xPositionCompareText}, -60)`)

        chartTooltip
          .select(classes.tooltip.percent)
          .classed('positive', statusTooltip === 'positive')
          .classed('negative', statusTooltip === 'negative')
          .classed('no-variation', statusTooltip === 'no-variation')
          .classed('no-available', statusTooltip === 'no-available')
          .text(previousPercentage)
          .style('opacity', '1')

        const rotateFromStatus = arrowRotation[statusTooltip] || 0
        groupArrow
          .classed('positive', statusTooltip === 'positive')
          .classed('negative', statusTooltip === 'negative')
          .classed('no-variation', statusTooltip === 'no-variation')
          .classed('no-available', statusTooltip === 'no-available')
          .attr('transform', `translate(-26, -73), rotate(${rotateFromStatus}, 7.5, 8)`)
          .style('opacity', '1')

        groupMinus
          .classed('no-available', statusTooltip === 'no-available')
          .attr('transform', `translate(-26, -73), rotate(${rotateFromStatus}, 8, 8)`)
          .style('opacity', '1')
      }
    }
  }

  function _handleResizeLineChart () {
    $window.addEventListener('resize', Utils.debounce(() => {
      CONSTANTS.width = checkWidthScreen($window.innerWidth, () => {
        api._loadDataFrameData(slug, $scope.data.id)
      }, $scope.fullWidth)
    }))
  }

  function _getDefaultDateFormat (dropdownList) {
    if (dropdownList.length) {
      return dropdownList.reduce((accumulator, dropdown) => {
        if (!accumulator && dropdown.defaultValue === 'TRUE' && $scope.flags.isFirstLoad) {
          accumulator = dropdown.queryValue
        }

        return accumulator
      }, '')
    }

    return 'DAILY'
  }
}

export { AnalyticsDataFrameLineChartController }
